import { Suspense, useEffect, useState, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { HuddleProvider } from "@huddle01/react";

import { useIsMobile } from "@/hooks/use-is-mobile";
import { usePresenceHeartbeat } from "@/hooks/use-presence-heartbeat";
import { registerTypingPublisher } from "@/realtime/typing-publisher";
import { useSlumbrNotifications } from "@slumbr/app-sdk";
import { useNotificationStore } from "@slumbr/core";

// One-time install of the direct-Momento typing publisher. Runs at module
// load so any component calling `sendChatTyping` from @slumbr/core gets
// the real implementation instead of the silent no-op fallback.
registerTypingPublisher();
import { TopBar } from "./top-bar";
import { DockDesktop } from "./dock/dock-desktop";
import { DockMobile } from "./dock/dock-mobile";
import { hidesAppBarOnTouch, isFullBleedRoute } from "./full-bleed-routes";
import { hideRevealedShellChrome, isShellChromeHidden, revealHold, useShellChrome } from "./shell-chrome";
import { registerShellDockWidgets } from "./dock/register-shell-widgets";
registerShellDockWidgets();
import { installRoomSessionTeardown } from "@/apps/games/kit/session-teardown";
// Game seats outlive their room pages; end them when the identity changes.
installRoomSessionTeardown();
import { NotificationPanel } from "./notification-panel";
import { MobileBottomNav } from "./mobile-bottom-nav";
import { IncomingCallOverlay } from "@/apps/messenger/components/incoming-call-overlay";
import { CallOverlay } from "@/apps/messenger/components/call-overlay";
import { getHuddleClient } from "@/apps/messenger/huddle01/config";

const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === "true";

const LoadingSpinner = () => (
  <div className="flex h-full items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
  </div>
);

/**
 * Wraps the shell tree in HuddleProvider so the CallOverlay (which lives at
 * shell level for cross-app persistence) can use Huddle01 hooks.
 * Skipped in DEV_AUTH_BYPASS mode where calls are mocked.
 */
function ShellHuddleProvider({ children }: { children: ReactNode }) {
  if (DEV_AUTH_BYPASS) return <>{children}</>;
  return <HuddleProvider client={getHuddleClient()}>{children}</HuddleProvider>;
}

export function ShellLayout() {
  return (
    <ShellHuddleProvider>
      <ShellLayoutInner />
    </ShellHuddleProvider>
  );
}

function ShellLayoutInner() {
  const isMobile = useIsMobile();
  const location = useLocation();
  // Hide the global bottom nav when the user is inside a chat conversation so
  // the conversation can fill the viewport (matches WhatsApp / iMessage).
  const inChatConversation = /^\/messenger\/[^/]+/.test(location.pathname);
  // Full-bleed game routes (Mimic pass-and-play, Card Table rooms) hide the dock and the mobile
  // bottom nav: the page owns the whole viewport and has its own exit.
  const fullBleed = isFullBleedRoute(location.pathname);
  const noAppBarOnTouch = hidesAppBarOnTouch(location.pathname);
  // Immersive mode (Villagr Settings ▸ Graphics): the route asked to hide the top bar, dock and bottom nav. Scoped to
  // its path prefix, so leaving the route restores the chrome. A hover/tap on the game's handle reveals the bar as
  // an overlay (no reflow of the game canvas) for a few seconds.
  const chrome = useShellChrome();
  const immersive = isShellChromeHidden(chrome, location.pathname);
  const revealBar = immersive && chrome.revealed;
  // The revealed bar stays while the pointer or focus is on it (revealHold); a touch reveal ends on a tap in the game.
  // Desktop: the inbox panel opens at top-0 under the revealed bar (z-50 vs z-130) — drop the bar so its header shows.
  const inboxOpen = useNotificationStore((s) => s.isOpen);
  useEffect(() => { if (inboxOpen && !isMobile) hideRevealedShellChrome(); }, [inboxOpen, isMobile]);

  useSlumbrNotifications();
  usePresenceHeartbeat();

  // Hide the mobile widget dock in landscape (it overlaps full-screen landscape
  // games); the dock returns in portrait.
  const [mobileLandscape, setMobileLandscape] = useState(
    () => typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse) and (orientation: landscape)").matches,
  );
  useEffect(() => {
    const mq = matchMedia("(pointer: coarse) and (orientation: landscape)");
    const onChange = () => setMobileLandscape(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  // Touch devices wide enough for the desktop tree (landscape phones, tablets) also drop the top
  // bar in Card Table rooms: a 390 px-tall landscape phone can't spare it (the page has its own bar).
  const [coarsePointer, setCoarsePointer] = useState(() => typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse)").matches);
  useEffect(() => {
    const mq = matchMedia("(pointer: coarse)");
    const onChange = () => setCoarsePointer(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Embedded mode (`?embed=1`) — the native app loads a game route inside a
  // WebView. Render only the routed content (no top bar / sidebar / bottom
  // nav), so the embedded game owns the full viewport. HuddleProvider + the
  // overlays still come from the outer ShellLayout wrapper.
  const embed = new URLSearchParams(location.search).get("embed") === "1";
  if (embed) {
    return (
      <div className="h-[100dvh] max-h-[100dvh] overflow-hidden bg-surface-0 dark:bg-surface-dark-0">
        <Suspense fallback={<LoadingSpinner />}>
          <Outlet />
        </Suspense>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-surface-0 dark:bg-surface-dark-0">
        {/* Mobile: scrollable content sandwiched between persistent bar + bottom nav (both fixed by flex layout).
            The bottom nav pads the home indicator, so content below it needn't (see games kit SAFE_BOTTOM) — except in
            immersive mode, where the nav is hidden and the page pads the home indicator itself. */}
        <main className={"min-h-0 flex-1 overflow-auto " + (immersive ? "[--shell-safe-bottom:env(safe-area-inset-bottom)]" : "[--shell-safe-bottom:0px]")}>
          <Suspense fallback={<LoadingSpinner />}>
            <Outlet />
          </Suspense>
        </main>
        {!inChatConversation && !mobileLandscape && !fullBleed && !immersive && <DockMobile />}
        {!inChatConversation && !fullBleed && !immersive && <MobileBottomNav />}
        {revealBar && (
          <div className="fixed inset-x-0 bottom-0 z-[130]" {...revealHold}>
            <MobileBottomNav />
          </div>
        )}
        <NotificationPanel />
        <IncomingCallOverlay />
        <CallOverlay />
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-surface-0 dark:bg-surface-dark-0">
      {/* Desktop: unified top bar + content */}
      {!(noAppBarOnTouch && coarsePointer) && !immersive && <TopBar />}
      {revealBar && (
        <div className="fixed inset-x-0 top-0 z-[130]" {...revealHold}>
          <TopBar />
        </div>
      )}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <main className="flex-1 overflow-auto">
          <Suspense fallback={<LoadingSpinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      {!fullBleed && !immersive && <DockDesktop />}
      <NotificationPanel />
      <IncomingCallOverlay />
      <CallOverlay />
    </div>
  );
}
